/**
 * FleetHub Main Screen - Web Version
 *
 * Main dashboard for B2B fleet customers.
 * Displays map with bottom sheet containing orders.
 *
 * @module screens/FleetHubScreen
 */

import { FC, useEffect, useState, ComponentType } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  Platform,
  Pressable,
  useWindowDimensions,
  Modal,
} from "react-native"
import { useNavigation } from "@react-navigation/native"
import type { NativeStackNavigationProp } from "@react-navigation/native-stack"
import * as Sentry from "@sentry/react"
import { onAuthStateChanged } from "firebase/auth"
import { FileText, Tag, LogOut, Menu, X, KeyRound } from "lucide-react"

import { BillingDropdown } from "@/components/BillingDropdown.web"
import { BookInspectionModal } from "@/components/BookInspectionModal"
import { Button } from "@/components/Button"
import { ButtonAccessoryProps } from "@/components/Button"
import { CancelConfirmationModal } from "@/components/CancelConfirmationModal"
import { ChangePasswordModal } from "@/components/ChangePasswordModal.web"
import {
  $header,
  $headerLeft,
  $headerSubtitle,
  $headerButtons,
  $headerButtonBase,
  $headerButtonHover,
  $headerButtonText,
  $iconAccessoryStyle,
  $headerMobile,
  $hamburgerButton,
  $mobileMenuBackdrop,
  $mobileMenuDropdown,
  $mobileMenuItem,
  $mobileMenuItemHover,
  $mobileMenuItemText,
  $mobileMenuDivider,
} from "@/components/fleetHubHeader.styles"
import { FleetHubLogo } from "@/components/FleetHubLogo"
import { FleetOrdersBottomSheet } from "@/components/FleetOrdersBottomSheet.web"
import { OnboardingTour } from "@/components/OnboardingTour.web"
import { PasswordChangeBanner } from "@/components/PasswordChangeBanner.web"
import { RescheduleInspectionModal } from "@/components/RescheduleInspectionModal"
import { Text } from "@/components/Text"
import { TrackingMapView } from "@/components/TrackingMapView"
import type { AppStackParamList } from "@/navigators/AppNavigator"
import { auth, waitForAuthReady } from "@/services/firebase/auth"
import { downloadPDFReport } from "@/services/firebase/functions"
import { logoutUser } from "@/services/firebase/logout"
import { type FleetOrder } from "@/stores/useFleetHubStore"
import { useFleetHubStore } from "@/stores/useFleetHubStore"
import { colors, spacing, breakpoints } from "@/theme"
import { openTrustedUrl } from "@/utils/openTrustedUrl"

export interface FleetHubScreenProps {
  /**
   * Callback for logout
   */
  onLogout?: () => void

  /**
   * Company name to display in header
   */
  companyName?: string
}

/**
 * FleetHub Main Screen - Web Version
 *
 * Displays map with bottom sheet containing orders.
 */
export const FleetHubScreen: FC<FleetHubScreenProps> = ({ onLogout, companyName }) => {
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>()
  const { width, height: windowHeight } = useWindowDimensions()
  const isMobile = width < breakpoints.mobile

  // Zustand store hooks
  const loadOrders = useFleetHubStore((state) => state.loadOrders)
  const loadMapData = useFleetHubStore((state) => state.loadMapData)
  const customerLocations = useFleetHubStore((state) => state.customerLocations)
  const hubLocations = useFleetHubStore((state) => state.hubLocations)
  const openRescheduleModal = useFleetHubStore((state) => state.openRescheduleModal)
  const closeRescheduleModal = useFleetHubStore((state) => state.closeRescheduleModal)
  const openCancelModal = useFleetHubStore((state) => state.openCancelModal)
  const closeCancelModal = useFleetHubStore((state) => state.closeCancelModal)
  const cancelFleetOrder = useFleetHubStore((state) => state.cancelFleetOrder)
  const isCancellingOrder = useFleetHubStore((state) => state.isCancellingOrder)
  const cancelError = useFleetHubStore((state) => state.cancelError)
  const showRescheduleModal = useFleetHubStore((state) => state.showRescheduleModal)
  const showCancelModal = useFleetHubStore((state) => state.showCancelModal)
  const showBookingModal = useFleetHubStore((state) => state.showBookingModal)
  const closeBookingModal = useFleetHubStore((state) => state.closeBookingModal)
  const isBookingOrder = useFleetHubStore((state) => state.isBookingOrder)
  const bookingError = useFleetHubStore((state) => state.bookingError)
  const selectedOrder = useFleetHubStore((state) => state.selectedOrder)
  const b2bAccessRevoked = useFleetHubStore((state) => state.b2bAccessRevoked)

  // Tutorial hooks
  const initializeTutorial = useFleetHubStore((state) => state.initializeTutorial)

  // Live tracking hooks
  const initializeLiveTracking = useFleetHubStore((state) => state.initializeLiveTracking)
  const stopLiveTracking = useFleetHubStore((state) => state.stopLiveTracking)
  const activeOrders = useFleetHubStore((state) => state.activeOrders)
  const trackingError = useFleetHubStore((state) => state.trackingError)
  const failedOrderSubscriptions = useFleetHubStore((state) => state.failedOrderSubscriptions)

  // Password change prompt hooks
  const showPasswordPrompt = useFleetHubStore((state) => state.showPasswordPrompt)
  const dismissPasswordPrompt = useFleetHubStore((state) => state.dismissPasswordPrompt)
  const markPasswordChanged = useFleetHubStore((state) => state.markPasswordChanged)

  // Logout confirmation modal state
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)

  // Billing dropdown state
  const [showBillingDropdown, setShowBillingDropdown] = useState(false)

  // Change password modal state
  const [showChangePassword, setShowChangePassword] = useState(false)

  // Hamburger menu state (mobile: full menu, desktop: account actions only)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  // Bottom sheet height for map bounds calculation
  const [bottomSheetHeight, setBottomSheetHeight] = useState<number | undefined>(undefined)

  // Header height for map bounds calculation
  const [headerHeight, setHeaderHeight] = useState<number | undefined>(undefined)

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        console.log("FleetHub: No authenticated user, redirecting to login")
        navigation.replace("FleetHubLogin")
      }
    })

    return () => unsubscribe()
  }, [navigation])

  // PLA-534 — poison-session recovery.
  //
  // The Firebase session is perfectly valid; it just does not belong to a B2B
  // customer (an internal ops account, or one deactivated or never fully
  // provisioned). Nothing else signs the user out, and browserLocalPersistence
  // keeps the session alive indefinitely — so without this the user is stuck on
  // a dashboard where every callable returns 403, and the root route forwards
  // them straight back here on the next visit.
  useEffect(() => {
    if (!b2bAccessRevoked) return
    void (async () => {
      try {
        await logoutUser()
      } catch (error) {
        // Redirect regardless — staying signed in is the worse failure here.
        // LOGFIX-PLA-600: this is an unexpected failure (not a validation/expected
        // outcome) on the security-relevant access-recovery path — bridge it to
        // Sentry (ADR-0016) instead of leaving it in a console.error nobody sees.
        console.error("[FleetHub] Logout during access recovery failed:", error)
        Sentry.captureException(error, {
          tags: { flow: "fleethub", operation: "access-recovery-logout" },
        })
      }
      navigation.replace("FleetHubLogin", { reason: "b2b-access-revoked" })
    })()
  }, [b2bAccessRevoked, navigation])

  // Load orders, map data, and initialize live tracking on mount.
  // Awaits auth.authStateReady() so callables never fire before Firebase has
  // rehydrated browserLocalPersistence (~50-500 ms race on cold page load).
  useEffect(() => {
    const initialize = async () => {
      try {
        await waitForAuthReady()
        if (!auth.currentUser) {
          navigation.replace("FleetHubLogin")
          return
        }
        await loadOrders(1)
        await loadMapData()
        // Initialize live tracking after orders are loaded
        await initializeLiveTracking()
        // Start onboarding tutorial after a short delay
        setTimeout(() => initializeTutorial(), 800)
      } catch (error) {
        console.error("[FleetHub] Error during initialization:", error)
        // TODO: Display error to user via toast/notification
      }
    }

    initialize()

    // Cleanup: stop live tracking on unmount
    return () => {
      try {
        stopLiveTracking()
      } catch (error) {
        console.error("[FleetHub] Error during cleanup:", error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]) // navigation stable ref; re-run if navigator identity changes

  // Monitor tracking errors
  useEffect(() => {
    if (trackingError) {
      console.error("[FleetHub] Live tracking error:", trackingError)

      if (failedOrderSubscriptions.length > 0) {
        console.error("[FleetHub] Failed order subscriptions:", failedOrderSubscriptions)
      }

      // TODO: Display error notification to user
      // Example: toast.error(`Live tracking error: ${trackingError}`)
    }
  }, [trackingError, failedOrderSubscriptions])

  // Debug logging
  useEffect(() => {
    console.log("[DEBUG FleetHub] Hub locations:", hubLocations)
    console.log("[DEBUG FleetHub] Hub locations length:", hubLocations.length)
    console.log("[DEBUG FleetHub] Customer locations:", customerLocations)
    console.log("[DEBUG FleetHub] Customer locations length:", customerLocations.length)
  }, [hubLocations, customerLocations])

  // Set document title and inject mobile menu animation for web
  useEffect(() => {
    if (Platform.OS === "web" && typeof document !== "undefined") {
      document.title = "FleetHub Dashboard"

      if (!document.getElementById("mobile-menu-animations")) {
        const style = document.createElement("style")
        style.id = "mobile-menu-animations"
        style.textContent = `
          @keyframes mobileMenuIn {
            from { opacity: 0; transform: translateY(-8px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `
        document.head.appendChild(style)
      }
    }
  }, [])

  // Close menu on resize (layout changes may reposition the dropdown)
  useEffect(() => {
    setShowMobileMenu(false)
  }, [isMobile])

  useEffect(() => {
    if (!showMobileMenu) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowMobileMenu(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [showMobileMenu])

  /**
   * Handle logout - show confirmation modal
   */
  const handleLogout = () => {
    setShowLogoutConfirmation(true)
  }

  /**
   * Confirm logout and execute
   */
  const confirmLogout = async () => {
    try {
      await logoutUser()
      setShowLogoutConfirmation(false)
      navigation.replace("FleetHubLogin")
    } catch (err: any) {
      alert(`Fehler beim Abmelden: ${err.message}`)
    }
  }

  /**
   * Handle opening pricing page
   */
  const handleOpenPreise = () => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open("https://www.holdeineplakette.de/preise", "_blank")
    }
  }

  /**
   * Handle PDF download
   */
  const handleDownloadPdf = async (order: FleetOrder) => {
    if (!order.trackingToken || !order.berichtPdfId) {
      alert("PDF-Bericht nicht verfügbar")
      return
    }

    try {
      const result = await downloadPDFReport(order.id, order.trackingToken)

      // Open PDF in new tab (https-only + noopener, PLA-514)
      if (Platform.OS === "web" && !openTrustedUrl(result.url)) {
        const err = new Error("Ungültiger Download-Link")
        Sentry.captureException(err, {
          tags: { surface: "fleethub", operation: "pdf_download" },
        })
        throw err
      }
    } catch (err: any) {
      alert(`Fehler beim Laden des Berichts: ${err.message}`)
    }
  }

  /**
   * Handle tracking link
   */
  const handleOpenTracking = (order: FleetOrder) => {
    if (!order.trackingToken) {
      alert("Tracking nicht verfügbar")
      return
    }

    // Open tracking page in new tab
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(`/track/${order.trackingToken}`, "_blank")
    }
  }

  /**
   * Handle cancel confirmation
   */
  const handleConfirmCancel = async () => {
    if (!selectedOrder) return

    try {
      await cancelFleetOrder(selectedOrder.id)
    } catch (err: any) {
      console.error("Error cancelling order:", err)
      // Error is handled in the store and shown in the modal
    }
  }

  /**
   * Handle "reschedule instead" from cancel modal
   */
  const handleRescheduleInstead = () => {
    if (!selectedOrder) return

    // Close cancel modal and open reschedule modal
    closeCancelModal()
    openRescheduleModal(selectedOrder)
  }

  /**
   * Icon accessories for buttons
   */
  const PreiseIcon = (props: ButtonAccessoryProps) => {
    const iconColor = props.disabled
      ? colors.palette.neutral500 // Muted gray for disabled
      : colors.palette.neutral100 // White for normal state

    return (
      <View style={[props.style, { marginEnd: spacing.sm }]}>
        <Tag size={20} color={iconColor} strokeWidth={2} />
      </View>
    )
  }

  const BillingIcon = (props: ButtonAccessoryProps) => {
    const iconColor = props.disabled ? colors.palette.neutral500 : colors.palette.neutral100

    return (
      <View style={[props.style, { marginEnd: spacing.sm }]}>
        <FileText size={20} color={iconColor} strokeWidth={2} />
      </View>
    )
  }

  /**
   * Custom header button component with brand red hover state
   */
  const HeaderButton: FC<{
    text: string
    LeftAccessory: ComponentType<ButtonAccessoryProps>
    onPress: () => void
    accessibilityLabel: string
    accessibilityHint: string
  }> = ({ text, LeftAccessory, onPress, accessibilityLabel, accessibilityHint }) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
      <Pressable
        onPress={onPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityHint={accessibilityHint}
        style={[$headerButtonBase, isHovered && $headerButtonHover]}
      >
        <LeftAccessory
          style={$iconAccessoryStyle}
          pressableState={{ pressed: false }}
          disabled={false}
        />
        <Text style={$headerButtonText}>{text}</Text>
      </Pressable>
    )
  }

  /**
   * Mobile menu item with icon and label
   */
  const MobileMenuItem: FC<{
    icon: React.ReactNode
    label: string
    onPress: () => void
  }> = ({ icon, label, onPress }) => {
    const [isHovered, setIsHovered] = useState(false)

    return (
      <Pressable
        onPress={onPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        accessibilityRole="button"
        accessibilityLabel={label}
        style={[$mobileMenuItem, isHovered && $mobileMenuItemHover]}
      >
        {icon}
        <Text style={$mobileMenuItemText}>{label}</Text>
      </Pressable>
    )
  }

  return (
    <View style={$root}>
      {/* Map takes exactly 100% of screen height */}
      <View style={[$mapContainer, { height: windowHeight }]}>
        <TrackingMapView
          driverLocation={null}
          customerLocation={null}
          pickupLocation={null}
          customerAddress={undefined}
          showRoute={false}
          routeToCustomer={null}
          orderPhase={undefined}
          isDriverReturning={false}
          isDriveby={false}
          allLocations={hubLocations}
          showAllLocations={true}
          fleetHubCustomerLocations={customerLocations}
          activeOrders={activeOrders}
          showMultipleOrders={true}
          bottomSheetHeight={bottomSheetHeight}
          headerHeight={headerHeight}
        />

        {/* Header - positioned on top of map */}
        <View
          style={[$header, isMobile && $headerMobile]}
          onLayout={(e) => setHeaderHeight(e.nativeEvent.layout.height)}
        >
          <View style={$headerLeft}>
            <Pressable
              onPress={() => navigation.navigate("FleetHub")}
              style={{ cursor: "pointer" as any }}
              accessibilityRole="button"
              accessibilityLabel="Zur FleetHub Startseite"
            >
              <FleetHubLogo width={isMobile ? 120 : 150} />
            </Pressable>
            {companyName && (
              <Text style={$headerSubtitle} numberOfLines={1} ellipsizeMode="tail">
                {companyName}
              </Text>
            )}
          </View>
          <View style={$headerButtons}>
            {/* Preise and Rechnungen only visible on desktop */}
            {!isMobile && (
              <>
                <HeaderButton
                  text="Preise"
                  LeftAccessory={PreiseIcon}
                  onPress={handleOpenPreise}
                  accessibilityLabel="Preise öffnen"
                  accessibilityHint="Öffnet die Preisseite in einem neuen Tab"
                />
                <View nativeID="tutorial-billing-btn">
                  <HeaderButton
                    text="Rechnungen"
                    LeftAccessory={BillingIcon}
                    onPress={() => setShowBillingDropdown((prev) => !prev)}
                    accessibilityLabel="Rechnungen anzeigen"
                    accessibilityHint="Zeigt Ihre Rechnungsdokumente an"
                  />
                </View>
              </>
            )}
            {/* Burger menu for account actions (all sizes) */}
            <Pressable
              onPress={() => setShowMobileMenu((prev) => !prev)}
              accessibilityRole="button"
              accessibilityLabel={showMobileMenu ? "Menü schließen" : "Menü öffnen"}
              style={$hamburgerButton}
              nativeID="tutorial-hamburger"
            >
              {showMobileMenu ? (
                <X size={22} color={colors.palette.neutral100} strokeWidth={2} />
              ) : (
                <Menu size={22} color={colors.palette.neutral100} strokeWidth={2} />
              )}
            </Pressable>
          </View>
        </View>

        {/* Password change prompt banner */}
        {showPasswordPrompt && (
          <PasswordChangeBanner
            onChangePassword={() => setShowChangePassword(true)}
            onDismiss={dismissPasswordPrompt}
            style={{ top: headerHeight ?? 60 }}
          />
        )}
      </View>

      {/* Hamburger menu dropdown (mobile: all items, desktop: account actions only) */}
      {showMobileMenu && (
        <>
          <Pressable
            style={$mobileMenuBackdrop}
            onPress={() => setShowMobileMenu(false)}
            accessibilityRole="button"
            accessibilityLabel="Menü schließen"
          />
          <View style={$mobileMenuDropdown}>
            {/* Preise & Rechnungen only in mobile menu (on desktop they're header buttons) */}
            {isMobile && (
              <>
                <MobileMenuItem
                  icon={<Tag size={20} color={colors.palette.neutral800} strokeWidth={2} />}
                  label="Preise"
                  onPress={() => {
                    setShowMobileMenu(false)
                    handleOpenPreise()
                  }}
                />
                <View style={$mobileMenuDivider} />
                <MobileMenuItem
                  icon={<FileText size={20} color={colors.palette.neutral800} strokeWidth={2} />}
                  label="Rechnungen"
                  onPress={() => {
                    setShowMobileMenu(false)
                    setShowBillingDropdown((prev) => !prev)
                  }}
                />
                <View style={$mobileMenuDivider} />
              </>
            )}
            <MobileMenuItem
              icon={<KeyRound size={20} color={colors.palette.neutral800} strokeWidth={2} />}
              label="Passwort ändern"
              onPress={() => {
                setShowMobileMenu(false)
                setShowChangePassword(true)
              }}
            />
            <View style={$mobileMenuDivider} />
            <MobileMenuItem
              icon={<LogOut size={20} color={colors.palette.neutral800} strokeWidth={2} />}
              label="Abmelden"
              onPress={() => {
                setShowMobileMenu(false)
                ;(onLogout || handleLogout)()
              }}
            />
          </View>
        </>
      )}

      {/* Billing Documents Dropdown */}
      <BillingDropdown
        visible={showBillingDropdown}
        onClose={() => setShowBillingDropdown(false)}
      />

      {/* Bottom Sheet - overlay on map */}
      <FleetOrdersBottomSheet
        onDownloadPdf={handleDownloadPdf}
        onOpenTracking={handleOpenTracking}
        onReschedule={openRescheduleModal}
        onCancel={openCancelModal}
        onHeightChange={setBottomSheetHeight}
      />

      {/* Modals */}
      <RescheduleInspectionModal
        visible={showRescheduleModal}
        onClose={closeRescheduleModal}
        order={selectedOrder}
      />

      <CancelConfirmationModal
        visible={showCancelModal}
        onClose={closeCancelModal}
        onConfirmCancel={handleConfirmCancel}
        onRescheduleInstead={handleRescheduleInstead}
        isLoading={isCancellingOrder}
        errorMessage={cancelError}
      />

      <BookInspectionModal
        visible={showBookingModal}
        onClose={closeBookingModal}
        isBookingOrder={isBookingOrder}
        bookingError={bookingError}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        visible={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onPasswordChanged={markPasswordChanged}
      />

      {/* Onboarding Tutorial */}
      <OnboardingTour />

      {/* Logout Confirmation Modal */}
      <Modal visible={showLogoutConfirmation} transparent animationType="fade">
        <Pressable style={$logoutModalOverlay} onPress={() => setShowLogoutConfirmation(false)}>
          <Pressable style={$logoutModalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={$logoutModalTitle}>Abmelden?</Text>
            <Text style={$logoutModalMessage}>
              Willst du dich wirklich vom FleetHub-Dashboard abmelden?
            </Text>
            <View style={$logoutModalButtons}>
              <Button
                text="Abbrechen"
                onPress={() => setShowLogoutConfirmation(false)}
                preset="default"
                style={$logoutModalButton}
              />
              <Button
                text="Abmelden"
                onPress={confirmLogout}
                preset="filled"
                style={[$logoutModalButton, $logoutModalButtonPrimary]}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  )
}

// Styles
const $root: ViewStyle = {
  flex: 1,
  backgroundColor: colors.surface,
  position: "relative",
}

const $mapContainer: ViewStyle = {
  width: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
}

// Logout confirmation modal styles
const $logoutModalOverlay: ViewStyle = {
  flex: 1,
  backgroundColor: colors.overlay,
  justifyContent: "center",
  alignItems: "center",
  padding: spacing.xl,
}

const $logoutModalContent: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 12,
  padding: spacing.xl,
  width: "100%",
  maxWidth: 400,
  // @ts-ignore - Web-specific
  boxShadow: "0 8px 24px rgba(17, 30, 44, 0.25)",
}

const $logoutModalTitle: TextStyle = {
  fontSize: 20,
  fontWeight: "700",
  color: colors.text,
  marginBottom: spacing.sm,
}

const $logoutModalMessage: TextStyle = {
  fontSize: 15,
  color: colors.textDim,
  marginBottom: spacing.lg,
  lineHeight: 22,
}

const $logoutModalButtons: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm,
  justifyContent: "flex-end",
}

const $logoutModalButton: ViewStyle = {
  minWidth: 100,
}

const $logoutModalButtonPrimary: ViewStyle = {
  backgroundColor: colors.primary,
}
